/* eslint-disable camelcase */
import { useRef, useState } from 'react'

import { Premiums } from '../../@types/RunPetisSimulation'
import { useSimulatorStore } from '../../Stores/simulatorStore'
import { useRunPetisSimulationStore } from '../../Stores/useRunPetisSimulationStore'
import { ReactComponent as ClosedArrow } from '../../assets/svgs/closed_row_arrow.svg'
import { ReactComponent as OpenedArrow } from '../../assets/svgs/opened_row_arrow.svg'
import { MIN_PRICE } from '../../constants/minPrice'
import { PRICE_TYPE } from '../../constants/priceType'
import { useClickedOutside } from '../../hooks/useClickedOutside'
import { useReferrals } from '../../hooks/useReferrals'
import { currencyFormatter } from '../../utils/currencyFormatter'
import { DropDownTable, OptionDropdown } from '../DropDownTable'
import { PackPriceHeader, PriceContainer, PriceDropDown } from './styles'
import { useTotalPriceAnimalsInstallment } from './useTotalPriceAnimalsInstallment'

type PriceProps = {
  disabled?: boolean
  isVisible: boolean
  prices: Premiums[]
  currentPrice: string
  packId?: string
  headerId: string
  expandPriceItem: string
  onClick: (value: string | number) => void
  setExpandPriceItem: (value: string) => void
}

type ArrowPriceProps = {
  expandPrice: boolean
  expandPriceItem: string
  headerId: string
  onClick: () => void
}

const PREMIUMS_CODE = ['MTL', 'QTL', 'BNN', 'ANN']

export const findMinInsuredObjectTotalPremiumValue = (premiums: Premiums[]) => {
  return premiums.reduce((min, current) => {
    const currentValue = current?.invoiceRefPremium || 0
    return currentValue < min ? currentValue : min
  }, Infinity)
}

const ArrowPrice = ({
  onClick,
  expandPrice,
  expandPriceItem,
  headerId,
}: ArrowPriceProps) => (
  <div style={{ padding: 8 }} onClick={onClick}>
    {expandPrice && expandPriceItem === headerId ? (
      <OpenedArrow />
    ) : (
      <ClosedArrow />
    )}
  </div>
)

export const Prices = ({
  disabled,
  prices,
  currentPrice,
  onClick,
  headerId,
  packId,
  expandPriceItem,
  setExpandPriceItem,
}: PriceProps) => {
  const togglePriceRef = useRef<HTMLDivElement>(null)
  const [expandPrice, setExpandPrice] = useState(false)
  const { typeOfPrice, isRecalculate } = useSimulatorStore()
  const { referralsStop } = useReferrals()
  const { previousPackCampaign } = useRunPetisSimulationStore()
  const {
    handleTotalPriceAnimalsInstallment,
    handleTotalPriceAnimalsInstallmentAllPremiums,
  } = useTotalPriceAnimalsInstallment()

  const totalPriceAnimalsInstallment = handleTotalPriceAnimalsInstallment(
    packId || '',
  )

  const totalPriceAnimalsInstallmentAllPremiums =
    handleTotalPriceAnimalsInstallmentAllPremiums(packId || '')

  const isReferralStop =
    (referralsStop || [])?.filter((item) => item?.packId === headerId)?.length >
    0

  useClickedOutside(expandPrice, setExpandPrice, togglePriceRef)

  const formatterOptions = () => {
    const sortedPrices = PREMIUMS_CODE.map((id) =>
      prices?.find((price) => price.installment === id),
    ).filter(Boolean)

    return sortedPrices
      .map((price) => {
        if (
          (price?.invoiceRefPremium || 0) <= MIN_PRICE &&
          (totalPriceAnimalsInstallmentAllPremiums || 0) <= MIN_PRICE
        ) {
          return null
        }

        return {
          id: price?.installment,
          value: `${currencyFormatter(price?.invoiceRefPremium || 0)}€/${
            PRICE_TYPE[price?.installment as never]
          }`,
          dropDownValue: (
            <PriceDropDown>
              <span>{PRICE_TYPE[price?.installment as never]}</span>{' '}
              <span>{currencyFormatter(price?.invoiceRefPremium || 0)}€</span>{' '}
            </PriceDropDown>
          ),
          isCurrentPrice:
            currentPrice === currencyFormatter(price?.invoiceRefPremium || 0),
        }
      })
      .filter(Boolean) as OptionDropdown[]
  }

  return (
    <PriceContainer ref={togglePriceRef}>
      <div className="price">
        {isReferralStop ||
        isRecalculate ||
        totalPriceAnimalsInstallment <= MIN_PRICE ? (
          <PackPriceHeader>
            <span className="empty">--</span>
          </PackPriceHeader>
        ) : (
          <>
            <PackPriceHeader>
              {currentPrice}€
              {typeOfPrice && (
                <span style={{ fontSize: 12 }}>
                  /{PRICE_TYPE[typeOfPrice as never]}
                </span>
              )}
            </PackPriceHeader>
            {!disabled && typeOfPrice && (
              <ArrowPrice
                headerId={headerId}
                expandPrice={expandPrice}
                expandPriceItem={expandPriceItem}
                onClick={() => {
                  setExpandPrice((prev) => !prev)
                  setExpandPriceItem(headerId)
                }}
              />
            )}
          </>
        )}
        {totalPriceAnimalsInstallment <= MIN_PRICE && previousPackCampaign && (
          <ArrowPrice
            headerId={headerId}
            expandPrice={expandPrice}
            expandPriceItem={expandPriceItem}
            onClick={() => {
              setExpandPrice((prev) => !prev)
              setExpandPriceItem(headerId)
            }}
          />
        )}
      </div>

      {expandPrice && typeOfPrice && (
        <div className="dropdown">
          <DropDownTable
            options={formatterOptions()}
            onClick={(value) => {
              onClick(value)
              setExpandPrice(false)
            }}
          />
        </div>
      )}
    </PriceContainer>
  )
}
